/**
 * Free Lift history + insight helpers.
 *
 * Small, client-side utilities the Free Lift surfaces use to feel
 * "feature-rich": last-time anchors, PR baselines, recent-exercise
 * quick-add, repeat-last-workout, and weekly muscle-balance.
 *
 * All reads go through the canonical-aware history layer (getSessionHistory
 * + resolveExerciseName) so a "Back Squat" logged today matches a
 * "Barbell Squat" from a program — see [[project_ml_recommendation_bug]].
 */

import { calculate1RMEpley } from './oneRM';
import { getSessionHistory, type PTSessionLog, type ExerciseLog } from './sessionStorage';
import { findExercise, resolveExerciseName, normalizeExerciseMatchKey, isAssistedExercise, type MuscleGroup } from './exerciseDatabase';

export interface LastPerformance {
  weight: number;
  reps: number;
  rpe: number | null;
  date: string | null; // ISO
}

export interface ExercisePriors {
  /** Top set of the most recent session that included this exercise. */
  last: LastPerformance | null;
  /** Best (heaviest) estimated 1RM ever recorded for this exercise. 0 = none. */
  bestE1RM: number;
}

/** Top working set of an ExerciseLog — heaviest weight, tie-break by reps.
 *  Skips warm-up sets so the "last time" anchor reflects real working load. */
function topSet(ex: ExerciseLog): { weight: number; reps: number; rpe: number | null } | null {
  if (!ex.sets || ex.sets.length === 0) return null;
  let best: { weight: number; reps: number; rpe: number | null } | null = null;
  for (const s of ex.sets) {
    if (!s || (s.weight ?? 0) <= 0 || (s.reps ?? 0) <= 0) continue;
    if (s.completed === false) continue;
    if ((s as any).kind === 'warmup') continue;
    if (!best || s.weight > best.weight || (s.weight === best.weight && s.reps > best.reps)) {
      best = { weight: s.weight, reps: s.reps, rpe: s.rpe ?? null };
    }
  }
  return best;
}

/** Find the ExerciseLog in a session that matches `targetKey` — top-level
 *  first, then inside any superset rounds. Canonical-aware. */
function findMatchingLog(session: PTSessionLog, targetKey: string): ExerciseLog | null {
  for (const ex of session.exercises) {
    if (matchesExercise(ex, targetKey)) return ex;
  }
  // Superset rounds store sub-exercises under exercises[].supersetRounds[].exercises
  for (const ex of session.exercises) {
    const rounds = (ex as any).supersetRounds;
    if (!Array.isArray(rounds)) continue;
    for (const round of rounds) {
      for (const sub of (round?.exercises ?? [])) {
        if (!sub?.name) continue;
        if (normalizeExerciseMatchKey(resolveExerciseName(sub.name)) === targetKey) {
          // Synthesize a minimal ExerciseLog from the matched sub-exercise.
          return {
            exerciseName: sub.name,
            sets: Array.isArray(sub.sets) ? sub.sets : [],
            actualRPE: sub.rpe ?? sub.actualRPE,
          } as ExerciseLog;
        }
      }
    }
  }
  return null;
}

export interface LastTimeRecord {
  dateISO: string;
  reps: number;
  weight: number;
  rpe?: number;
  distance?: number; // meters (cardio / ruck / carry)
}

/** Turn one matched ExerciseLog into a last-time record. Prefers the top
 *  working set (strength); falls back to log-level cardio actuals. */
function logToRecord(log: ExerciseLog, date: Date | string): LastTimeRecord | null {
  const top = topSet(log);
  const distSet = (log.sets ?? []).find(s => ((s as any).distance ?? 0) > 0) as any;
  const distance = distSet?.distance ?? (log as any).actualDistance;
  const reps = top?.reps ?? (log as any).actualReps ?? 0;
  const weight = top?.weight ?? (log as any).actualLoad ?? 0;
  const rpe = top?.rpe ?? (log as any).actualRPE ?? undefined;
  const hasDistance = typeof distance === 'number' && distance > 0;
  if (!top && !hasDistance && reps <= 0 && weight <= 0) return null;
  return {
    dateISO: new Date(date).toISOString(),
    reps, weight,
    ...(rpe != null ? { rpe } : {}),
    ...(hasDistance ? { distance } : {}),
  };
}

/**
 * The N most recent sessions that included this exercise, newest-first,
 * each reduced to its top working set (or cardio actuals). Canonical-aware
 * and superset-aware. This is the single source of truth for the
 * "Last time" / "vs last" / HISTORY surfaces — replaces the per-screen
 * bespoke matchers that missed aliases and returned oldest-first data.
 */
export async function getExerciseLastTimes(exerciseName: string, n = 3): Promise<LastTimeRecord[]> {
  try {
    const targetKey = normalizeExerciseMatchKey(resolveExerciseName(exerciseName));
    if (!targetKey) return [];
    const sessions = (await getSessionHistory({ all: true }))
      .filter((s): s is PTSessionLog => s.sessionType === 'pt' || s.sessionType === 'assessment')
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const out: LastTimeRecord[] = [];
    for (const session of sessions) {
      const log = findMatchingLog(session, targetKey);
      if (!log) continue;
      const rec = logToRecord(log, session.date);
      if (rec) out.push(rec);
      if (out.length >= n) break;
    }
    return out;
  } catch {
    return [];
  }
}

/** Does an ExerciseLog refer to the same movement as `targetKey`? */
function matchesExercise(ex: ExerciseLog, targetKey: string): boolean {
  return normalizeExerciseMatchKey(resolveExerciseName(ex.exerciseName)) === targetKey;
}

/**
 * Last-time set + best historical e1RM for one exercise. One pass over
 * the (cached) session history. Returns empty priors on any failure so
 * callers can render without a guard.
 */
export async function getExercisePriors(exerciseName: string): Promise<ExercisePriors> {
  const empty: ExercisePriors = { last: null, bestE1RM: 0 };
  try {
    const targetKey = normalizeExerciseMatchKey(resolveExerciseName(exerciseName));
    if (!targetKey) return empty;
    const sessions = (await getSessionHistory({ all: true }))
      .filter((s): s is PTSessionLog => s.sessionType === 'pt' || s.sessionType === 'assessment');

    let bestE1RM = 0;
    let last: LastPerformance | null = null;
    let lastTime = -Infinity;

    for (const session of sessions) {
      const when = new Date(session.date).getTime();
      for (const ex of session.exercises) {
        if (!matchesExercise(ex, targetKey)) continue;
        const top = topSet(ex);
        if (!top) continue;
        const e1rm = calculate1RMEpley(top.weight, top.reps);
        if (e1rm > bestE1RM) bestE1RM = e1rm;
        if (when > lastTime) {
          lastTime = when;
          last = { weight: top.weight, reps: top.reps, rpe: top.rpe, date: new Date(session.date).toISOString() };
        }
      }
    }
    // Assisted movements: the "weight" is assistance (higher = easier), so an
    // Epley e1RM is meaningless and a "PR" would fire on the MOST-assisted
    // (weakest) set. Suppress the e1RM/PR baseline; keep the last-time anchor.
    if (isAssistedExercise(exerciseName)) bestE1RM = 0;
    return { last, bestE1RM };
  } catch {
    return empty;
  }
}

/**
 * Compact human label for a last-performance, e.g. "185×8 · 3wk ago".
 *
 * `exerciseName` is optional but should be passed whenever it's available: it
 * lets a bodyweight movement render as reps even when the STORED row carries a
 * bogus weight. Legacy push-up rows were saved with a phantom per-set load and
 * bodyweight:false, so without this guard the chip reads "25×25 lb" for a set of
 * 25 push-ups. Current DB classification wins over stored weight — the stored
 * value is the corrupted side, and history is not being back-filled.
 */
export function formatLastPerformance(
  p: LastPerformance | null,
  exerciseName?: string
): string | null {
  if (!p) return null;
  const isBodyweight =
    !!exerciseName && findExercise(exerciseName)?.trackingType === 'bodyweight-reps';
  const wt = p.weight > 0 && !isBodyweight ? `${p.weight}×${p.reps}` : `${p.reps} reps`;
  const rpe = p.rpe != null ? ` @${p.rpe}` : '';
  let ago = '';
  if (p.date) {
    const days = Math.floor((Date.now() - new Date(p.date).getTime()) / 86_400_000);
    ago = days <= 0 ? ' · today'
      : days === 1 ? ' · yesterday'
      : days < 7 ? ` · ${days}d ago`
      : days < 28 ? ` · ${Math.round(days / 7)}wk ago`
      : ` · ${Math.round(days / 30)}mo ago`;
  }
  return `${wt}${rpe}${ago}`;
}

/**
 * Most-used exercise names from recent history, newest-weighted, for the
 * quick-add chips. Returns canonical display names.
 */
export async function getRecentExerciseNames(max = 8): Promise<string[]> {
  try {
    const sessions = (await getSessionHistory({ all: true }))
      .filter((s): s is PTSessionLog => s.sessionType === 'pt' || s.sessionType === 'assessment')
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 40); // last ~40 sessions is plenty

    const score = new Map<string, { name: string; score: number }>();
    sessions.forEach((s, idx) => {
      const recencyWeight = 1 / (idx + 1); // newer sessions count more
      for (const ex of s.exercises) {
        if (!ex.exerciseName) continue;
        const display = resolveExerciseName(ex.exerciseName);
        const key = normalizeExerciseMatchKey(display);
        if (!key) continue;
        const prev = score.get(key);
        score.set(key, { name: prev?.name ?? display, score: (prev?.score ?? 0) + recencyWeight });
      }
    });

    return Array.from(score.values()).sort((a, b) => b.score - a.score).slice(0, max).map(v => v.name);
  } catch {
    return [];
  }
}

export interface RepeatExercise {
  name: string;
  /** Inferred plan: sets × reps × weight from the last performance. */
  sets: number;
  reps: number;
  weight: number;
}

/**
 * The exercise list of the most recent Free Lift session, shaped for
 * re-seeding the builder ("Repeat last workout"). Weight/reps come from
 * each exercise's top completed set.
 */
export async function getLastFreeLiftWorkout(): Promise<{ date: string; exercises: RepeatExercise[] } | null> {
  try {
    const sessions = (await getSessionHistory({ all: true }))
      .filter((s): s is PTSessionLog =>
        (s.sessionType === 'pt' || s.sessionType === 'assessment') &&
        (s.programSelection === 'free-lift'))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const last = sessions[0];
    if (!last || !last.exercises?.length) return null;

    const exercises: RepeatExercise[] = [];
    for (const ex of last.exercises) {
      if (!ex.exerciseName) continue;
      const top = topSet(ex);
      const setCount = ex.sets?.length || 3;
      exercises.push({
        name: resolveExerciseName(ex.exerciseName),
        sets: setCount,
        reps: top?.reps ?? 10,
        weight: top?.weight ?? 0,
      });
    }
    if (!exercises.length) return null;
    return { date: new Date(last.date).toISOString(), exercises };
  } catch {
    return null;
  }
}

// ============================================================================
// MUSCLE BALANCE (week tracker)
// ============================================================================

/** Coarse training groups for the weekly balance view. */
export type TrainingGroup = 'Push' | 'Pull' | 'Legs' | 'Core' | 'Other';

const MUSCLE_TO_GROUP: Record<MuscleGroup, TrainingGroup> = {
  chest: 'Push', shoulders: 'Push', triceps: 'Push',
  back: 'Pull', biceps: 'Pull', forearms: 'Pull',
  quads: 'Legs', hamstrings: 'Legs', glutes: 'Legs', calves: 'Legs',
  core: 'Core', abs: 'Core', obliques: 'Core',
  'full-body': 'Other', 'cardio-specific': 'Other',
};

export const TRAINING_GROUPS: TrainingGroup[] = ['Push', 'Pull', 'Legs', 'Core'];

/** Map an exercise name → its training group via the DB's primary muscles. */
export function groupForExercise(name: string): TrainingGroup {
  const db = findExercise(name);
  const primary = db?.primaryMuscles?.[0];
  if (primary && MUSCLE_TO_GROUP[primary]) return MUSCLE_TO_GROUP[primary];
  return 'Other';
}

/**
 * Count of working sets per training group across a set of sessions —
 * the weekly muscle-balance signal. Cardio/ruck/timed exercises (no
 * weighted sets) contribute to 'Other' by their single effort.
 */
export function muscleBalance(sessions: any[]): Record<TrainingGroup, number> {
  const tally: Record<TrainingGroup, number> = { Push: 0, Pull: 0, Legs: 0, Core: 0, Other: 0 };
  for (const s of sessions || []) {
    for (const ex of (s?.exercises || [])) {
      const group = groupForExercise(ex?.exerciseName || ex?.name || '');
      const setCount = Array.isArray(ex?.sets)
        ? ex.sets.filter((st: any) => st && (st.completed !== false) && (st.reps ?? 0) > 0).length
        : 0;
      tally[group] += setCount > 0 ? setCount : 1;
    }
  }
  return tally;
}
